var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { Text, Button, Icon } from '..';
import { useTheme, ThemeProvider } from '../../theme';
import { useMultipleMemoClasses, useMemoClasses, usePortalContainer, useRenderTransition } from '../../utils/hooks';
var useStyles = createUseStyles({
    container: {
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
    },
    containerOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.container));
    },
    backdrop: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgb(0,0,0,0.5)',
        pointerEvents: 'all',
    },
    backdropOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.backdrop));
    },
    base: {
        position: 'relative',
        left: '50%',
        top: '10%',
        transform: 'translate(-50%, -50%) scale(0)',
        display: 'flex',
        flexDirection: 'column',
        opacity: 0,
        width: 'fit-content',
        maxHeight: '80%',
        backgroundColor: 'var(--RUI-color-paper)',
        borderRadius: 'var(--RUI-border-radius)',
        boxShadow: '0px 4px 16px -6px black',
        transition: function (_a) {
            var transitionDuration = _a.transitionDuration;
            return "all ".concat(transitionDuration, "s");
        },
        overflow: 'hidden',
        pointerEvents: 'all',
        '&$open': {
            transform: 'translateX(-50%) scale(1)',
            opacity: 1,
        },
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.base));
    },
    width: {
        width: '100%',
        maxWidth: function (_a) {
            var theme = _a.theme, width = _a.width;
            return "".concat(theme.breakpoints[width], "px");
        },
    },
    centered: {
        top: '50%',
        maxHeight: '100%',
        '&$open': {
            transform: 'translate(-50%, -50%) scale(1)',
            opacity: 1,
        },
    },
    open: {},
    openOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.open));
    },
    header: {
        borderBottom: 'var(--RUI-border-width) solid var(--RUI-color-neutral)',
        padding: 'var(--RUI-padding)',
        paddingRight: 'calc(2 * var(--RUI-padding))',
        textAlign: 'left',
        flexGrow: 0,
    },
    headerOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.header));
    },
    content: {
        padding: '0 var(--RUI-padding)',
        margin: 'calc(2 * var(--RUI-margin)) 0',
        overflow: 'auto',
        '&:first-child': {
            paddingTop: 'var(--RUI-padding)',
        },
    },
    contentOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.content));
    },
    footer: {
        padding: 'var(--RUI-padding)',
        paddingTop: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        flexGrow: 0,
    },
    footerOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.footer));
    },
    closeButton: {
        cursor: 'pointer',
        position: 'absolute',
        top: 'calc(0.5 * var(--RUI-margin))',
        right: 0,
        border: 'none',
        padding: 0,
        margin: 0,
        backgroundColor: 'transparent',
        '&:hover': {
            transform: 'scale(1.1)',
        },
    },
    closeButtonOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Modal.closeButton));
    },
}, { name: 'RUIModal' });
var Modal = function (_a) {
    var className = _a.className, children = _a.children, open = _a.open, persistent = _a.persistent, centered = _a.centered, backdrop = _a.backdrop, width = _a.width, header = _a.header, title = _a.title, footer = _a.footer, confirmText = _a.confirmText, cancelText = _a.cancelText, transitionDuration = _a.transitionDuration, onClose = _a.onClose, onConfirm = _a.onConfirm, onCancel = _a.onCancel, props = __rest(_a, ["className", "children", "open", "persistent", "centered", "backdrop", "width", "header", "title", "footer", "confirmText", "cancelText", "transitionDuration", "onClose", "onConfirm", "onCancel"]);
    var theme = useTheme();
    var styles = useStyles({ theme: theme, width: width, transitionDuration: transitionDuration });
    var _b = useRenderTransition(open, transitionDuration * 1000), shouldRender = _b.shouldRender, shouldApplyTransition = _b.shouldApplyTransition;
    var _c = useMultipleMemoClasses([
        [styles.container, theme.overrides.Modal.container && styles.containerOverrides, 'RUIModal-container'],
        [styles.backdrop, theme.overrides.Modal.backdrop && styles.backdropOverrides, 'RUIModal-backdrop'],
        [styles.header, theme.overrides.Modal.header && styles.headerOverrides, 'RUIModal-header'],
        [styles.content, theme.overrides.Modal.content && styles.contentOverrides, 'RUIModal-content'],
        [styles.footer, theme.overrides.Modal.footer && styles.footerOverrides, 'RUIModal-footer'],
        [styles.closeButton, theme.overrides.Modal.closeButton && styles.closeButtonOverrides, 'RUIModal-closeButton'],
    ], [theme]), containerClasses = _c[0], backdropClasses = _c[1], headerClasses = _c[2], contentClasses = _c[3], footerClasses = _c[4], closeButtonClasses = _c[5];
    var baseClasses = useMemoClasses([
        styles.base,
        theme.overrides.Modal.base && styles.baseOverrides,
        centered && styles.centered,
        width && styles.width,
        shouldApplyTransition && styles.open,
        shouldApplyTransition && theme.overrides.Modal.open && styles.openOverrides,
        'RUIModal-base',
        className,
    ], [theme, shouldApplyTransition, centered, width, className]);
    var portalContainer = usePortalContainer('Modal', true);
    var modal = shouldRender || persistent ? (React.createElement(ThemeProvider, { theme: theme },
        React.createElement("div", __assign({ className: containerClasses }, props),
            backdrop && shouldRender && React.createElement("span", { className: backdropClasses, onClick: onClose }),
            React.createElement("div", { className: baseClasses },
                !header && title ? (React.createElement("div", { className: headerClasses },
                    React.createElement(Text, { variant: "title5", strong: true, tag: "p", style: { margin: 0 } }, title))) : (header),
                React.createElement("div", { className: contentClasses }, children),
                footer === undefined
                    ? cancelText &&
                        confirmText && (React.createElement("div", { className: footerClasses },
                        React.createElement(Button, { variant: "outline", color: "neutral", onClick: onCancel, style: { marginLeft: 0, marginTop: 0, marginBottom: 0 } }, cancelText),
                        React.createElement(Button, { variant: "full", color: "primary", onClick: onConfirm, style: { marginRight: 0, marginTop: 0, marginBottom: 0 } }, confirmText)))
                    : footer,
                React.createElement("button", { "aria-label": "close", className: closeButtonClasses, onClick: onClose },
                    React.createElement(Icon, null, "close")))))) : null;
    return createPortal(modal, portalContainer);
};
Modal.defaultProps = {
    open: false,
    persistent: false,
    centered: false,
    backdrop: true,
    width: null,
    title: '',
    confirmText: '',
    cancelText: '',
    transitionDuration: 0.3,
};
export { Modal as default, Modal };
